import { useNavigate } from "react-router-dom";
import { useSuspenseQuery } from "@tanstack/react-query";
import { useState } from "react";
import { format, parseISO } from "date-fns";
import AdminDashboardStats from "../../Components/AdminDashboardStats";
import { getMemberOwnerHeaders, getRegionStatsAsync } from "../../services/adminService";
import { daysAgo } from "../../utilities/helperFunctions";
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from "../../Components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../Components/ui/table";
import { Badge } from "../../Components/ui/badge";
import Button from "../../Components/Button";
import { queryKeys } from "../../utilities/queryKeys";
import { getBadgeVariantByActionItem } from "../../utilities/stageRegistry";

// ─── Main Dashboard ──────────────────────────────────────────────────────────

function AdminDashboard() {
  const [viewAll, setViewAll] = useState(false);
  const navigate = useNavigate();

  const { data: memberHeadersData } = useSuspenseQuery({
    queryKey: queryKeys.dashboards.admin(),
    queryFn: () => getMemberOwnerHeaders(),
  });

  // Same query (and key) as AdminDashboardStats below — served from the shared
  // cache, so this adds no extra request. Used to keep the empty state truthful:
  // Action Items are owner-scoped, so "nothing here" must not read as "nothing
  // anywhere" while unowned applications await review (review R29).
  const { data: regionStatsData } = useSuspenseQuery({
    queryKey: queryKeys.dashboards.adminStats(),
    queryFn: () => getRegionStatsAsync(),
  });
  const awaitingReviewCount = regionStatsData.regionalDetails.awaitingReview;

  const memberHeaders = memberHeadersData.ownerMembers
    .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
    .map(h => ({
      ...h,
      daysInStage: h.stageChangedAt ? daysAgo(new Date(h.stageChangedAt)) : "-"
    }));

  const displayItems = viewAll ? memberHeaders : memberHeaders.slice(0, 5);
  const remaining = memberHeaders.length - 5;

  return (
    <div className="p-4 md:p-6 space-y-6">
      <h2>Welcome back{memberHeadersData.name ? `, ${memberHeadersData.name}!` : "!"}</h2>

      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg">Action Items</CardTitle>
            {remaining > 0 && !viewAll && (
              <span className="text-sm text-muted-foreground">{memberHeaders.length} total</span>
            )}
          </div>
        </CardHeader>
        <CardContent className="px-2 sm:px-6">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Startup</TableHead>
                <TableHead>Action Item</TableHead>
                <TableHead>Application Date</TableHead>
                <TableHead>Contact Person</TableHead>
                <TableHead className="text-right">Days in Stage</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {displayItems.map(item => (
                <TableRow
                  key={item.rowKey}
                  className="cursor-pointer"
                  onClick={() => navigate(`/dashboard/memberships/${item.id}`)}
                >
                  <TableCell className="font-medium">{item.companyName}</TableCell>
                  <TableCell>
                    <Badge variant={getBadgeVariantByActionItem(item.actionItem, 'admin')}>
                      {item.actionItem}
                    </Badge>
                  </TableCell>
                  <TableCell>{format(parseISO(String(item.date)), "MMM do yyyy")}</TableCell>
                  <TableCell>{item.contact}</TableCell>
                  <TableCell className="text-right">{item.daysInStage}</TableCell>
                </TableRow>
              ))}
              {memberHeaders.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5} className="text-center text-muted-foreground py-8">
                    {/* Truthful empty state (review R29): this list only shows
                        accounts YOU own — unowned work can still be waiting. */}
                    {awaitingReviewCount > 0
                      ? `No action items on accounts you own — but ${awaitingReviewCount} new ${awaitingReviewCount === 1 ? "application is" : "applications are"} awaiting review (see the "new applications awaiting review" card below).`
                      : "No action items on accounts you own. New submissions appear in the \"new applications awaiting review\" card below."}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
        {remaining > 0 && !viewAll && (
          <CardFooter className="justify-end gap-3 border-t pt-4">
            <span className="text-sm text-muted-foreground">{remaining} additional action items</span>
            <Button
              buttonText="View All"
              buttonType="secondary"
              actionHandler={() => setViewAll(true)}
            />
          </CardFooter>
        )}
      </Card>

      <div>
        <h3 className="text-lg font-semibold mb-4">Quick Facts</h3>
        <AdminDashboardStats />
      </div>
    </div>
  );
}

export default AdminDashboard;
